import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Loader2, AlertTriangle } from 'lucide-react';
import { useUploadStore, UploadTask } from '../../store/uploadStore';
import { uploadManager } from '../../services/uploadManager';
import { useLazyUploadThumbnail } from '../../lib/uploadThumbnails';
import { PhotoGrid } from '../shared/PhotoGrid';
import styles from './UploadingCards.module.css';

// ── Single uploading tile ───────────────────────────────────────────────────

interface UploadingCardProps {
  task: UploadTask;
  fillRef: (id: string, el: HTMLDivElement | null) => void;
  pctRef: (id: string, el: HTMLSpanElement | null) => void;
}

const UploadingCard = memo(function UploadingCard({ task, fillRef, pctRef }: UploadingCardProps) {
  const [imgError, setImgError] = useState(false);
  const cellRef = useLazyUploadThumbnail<HTMLDivElement>(task.id, !!task.previewUrl);
  const queued = task.status === 'queued';
  const uploading = task.status === 'uploading';
  const processing = task.status === 'done'; // uploaded; server still processing
  const failed = task.status === 'failed';

  return (
    <motion.div
      ref={cellRef}
      className={[styles['cell'], failed ? styles['cell-failed'] : ''].filter(Boolean).join(' ')}
      layout
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.92 }}
      transition={{ type: 'spring', stiffness: 420, damping: 32 }}
    >
      {task.previewUrl && !imgError ? (
        <img
          src={task.previewUrl}
          alt=""
          className={styles['img']}
          loading="lazy"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className={styles['img-fallback']} />
      )}

      <div className={styles['scrim']} aria-hidden />

      <div className={styles['status']}>
        {queued     && <Clock         className={styles['status-icon']} aria-hidden />}
        {uploading  && <Loader2       className={`${styles['status-icon']} ${styles['spin']}`} aria-hidden />}
        {processing && <Loader2       className={`${styles['status-icon']} ${styles['spin']}`} aria-hidden />}
        {failed     && <AlertTriangle className={styles['status-icon']} aria-hidden />}
        <span className={styles['status-label']}>
          {queued     && 'Queued'}
          {uploading  && <span ref={(el) => pctRef(task.id, el)}>0%</span>}
          {processing && 'Processing'}
          {failed     && 'Failed'}
        </span>
      </div>

      {(queued || uploading) && (
        <div className={styles['track']} aria-hidden>
          <div ref={(el) => fillRef(task.id, el)} className={styles['fill']} style={{ width: '0%' }} />
        </div>
      )}
    </motion.div>
  );
});

// ── Grid of in-flight uploads ────────────────────────────────────────────────

interface UploadingCardsProps {
  eventId: string;
  /** _ids of photos already in the gallery — matching 'done' tasks are hidden. */
  landedPhotoIds: Set<string>;
}

/**
 * Live tiles for photos being uploaded from this device, rendered at the top of
 * the gallery grid. Each tile shows the local preview + progress, then is
 * dropped once the processed photo lands in the gallery (or the task clears).
 *
 * Progress is pushed straight to DOM refs via uploadManager.subscribeProgress
 * so byte-level ticks never re-render this subtree.
 */
export function UploadingCards({ eventId, landedPhotoIds }: UploadingCardsProps) {
  const tasks = useUploadStore((s) => s.tasks);

  const fillRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const pctRefs = useRef<Map<string, HTMLSpanElement>>(new Map());

  const registerFill = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) fillRefs.current.set(id, el);
    else fillRefs.current.delete(id);
  }, []);
  const registerPct = useCallback((id: string, el: HTMLSpanElement | null) => {
    if (el) pctRefs.current.set(id, el);
    else pctRefs.current.delete(id);
  }, []);

  useEffect(() => {
    return uploadManager.subscribeProgress((id, pct) => {
      const fillEl = fillRefs.current.get(id);
      if (fillEl) fillEl.style.width = `${pct}%`;
      const pctEl = pctRefs.current.get(id);
      if (pctEl) pctEl.textContent = `${pct}%`;
    });
  }, []);

  const cards = tasks.filter((t) => {
    if (t.eventId !== eventId) return false;
    // Hide once the processed photo has arrived in the gallery below.
    if (t.status === 'done') return !(t.photo && landedPhotoIds.has(t.photo._id));
    return true; // queued | uploading | failed
  });

  if (cards.length === 0) return null;

  return (
    <PhotoGrid className={styles['grid']}>
      <AnimatePresence initial={false}>
        {cards.map((task) => (
          <UploadingCard key={task.id} task={task} fillRef={registerFill} pctRef={registerPct} />
        ))}
      </AnimatePresence>
    </PhotoGrid>
  );
}
